// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Finding a symbol by name.
//
// Half the people who open an API reference already know what they are looking
// for and only want its signature, so this is the main way in rather than a
// convenience bolted on the side.
//
// No search library. A package is at most a few hundred rows, already loaded
// as the index that draws the page, and ranking them is a scan over an array
// of lowercased strings - faster than the network call that would fetch a
// prebuilt trie, and one less dependency to keep current.
// ==========================================================================

import { ApiPackageIndex, ApiSymbolKind } from './types';

export interface SearchHit {
  /** `add_interp`, or `Solver.solve` for a method. */
  name: string;
  kind: ApiSymbolKind | 'mod';
  module: string;
  summary: string;
  /** Which package it came from, so one box can cover all three. */
  packageName: string;
}

export interface ScoredHit {
  hit: SearchHit;
  score: number;
}

// Lower is better. The gaps leave room to slot a rank in without renumbering.
const EXACT = 0;
const PREFIX = 10;
const CONTAINS = 20;
const IN_PATH = 30;
const IN_SUMMARY = 40;
const NO_MATCH = -1;

const rank = (haystack: string, needle: string, base: number): number => {
  if (haystack === needle) return EXACT;
  if (haystack.startsWith(needle)) return PREFIX + base;
  return haystack.includes(needle) ? CONTAINS + base : NO_MATCH;
};

/**
 * The lowercased columns the scan runs over.
 *
 * Built once per package rather than per keystroke: lowercasing a few hundred
 * strings on every character typed is the one thing here that would be felt.
 */
export interface SearchKeys {
  symbols: string[];
  modules: string[];
  summaries: string[];
}

export const buildSearchKeys = (index: ApiPackageIndex): SearchKeys => ({
  symbols: index.symbols.map((row) => row[0].toLowerCase()),
  modules: index.modules.map((entry) => entry.module.toLowerCase()),
  summaries: index.symbols.map((row) => row[3].toLowerCase())
});

/** Ranked but not truncated, so several packages can be merged fairly. */
export const searchScored = (
  index: ApiPackageIndex,
  keys: SearchKeys,
  query: string
): ScoredHit[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  const scored: ScoredHit[] = [];

  index.modules.forEach((entry, position) => {
    const score = rank(keys.modules[position], needle, 0);
    if (score !== NO_MATCH) {
      scored.push({
        hit: {
          name: entry.module,
          kind: 'mod',
          module: entry.module,
          summary: entry.summary,
          packageName: index.package
        },
        score
      });
    }
  });

  index.symbols.forEach((row, position) => {
    const [name, kind, moduleIndex, summary] = row;
    const module = index.modules[moduleIndex]?.module ?? '';

    // The symbol's own name first. Its module path and its summary can still
    // match, but rank below every symbol whose name did - somebody typing
    // "weights" wants the function called weights before the thirty functions
    // that merely mention weights.
    let score = rank(keys.symbols[position], needle, 0);
    if (score === NO_MATCH && module.toLowerCase().includes(needle)) {
      score = IN_PATH;
    }
    if (score === NO_MATCH && keys.summaries[position].includes(needle)) {
      score = IN_SUMMARY;
    }
    if (score === NO_MATCH) return;

    scored.push({
      hit: { name, kind, module, summary, packageName: index.package },
      score
    });
  });

  return scored;
};

/** Best first: score, then the shorter name, then alphabetical. */
export const byRelevance = (a: ScoredHit, b: ScoredHit): number =>
  a.score - b.score ||
  // A shorter name containing the query is the closer match: for "psd",
  // `psd` before `psd_data_from_selection`.
  a.hit.name.length - b.hit.name.length ||
  a.hit.name.localeCompare(b.hit.name);

export const searchSymbols = (
  index: ApiPackageIndex,
  keys: SearchKeys,
  query: string,
  limit = 50
): SearchHit[] =>
  searchScored(index, keys, query)
    .sort(byRelevance)
    .slice(0, limit)
    .map((entry) => entry.hit);

/**
 * One ranking across several packages.
 *
 * Merged on score rather than shown package by package: somebody searching for
 * a name does not know, and should not have to know, which of the three
 * packages it lives in. That is the answer, not the question.
 */
export const searchAll = (
  indexes: (ApiPackageIndex | null | undefined)[],
  keys: (SearchKeys | null | undefined)[],
  query: string,
  limit = 60
): SearchHit[] => {
  const merged: ScoredHit[] = [];
  indexes.forEach((index, position) => {
    const packageKeys = keys[position];
    if (index && packageKeys) {
      merged.push(...searchScored(index, packageKeys, query));
    }
  });
  return merged
    .sort(byRelevance)
    .slice(0, limit)
    .map((entry) => entry.hit);
};

/** How a hit is labelled in a result row. */
export const KIND_LABEL: Record<SearchHit['kind'], string> = {
  f: 'fn',
  c: 'class',
  m: 'method',
  mod: 'module'
};
